import { track } from 'lib/sdks/analytics';
import type { State } from './JobRefinement.reducer';
import type { QuestionFormat } from './types';
import { QUESTION_FORMAT } from './types';

export const JOB_REFINEMENT_INTERACTIONS = {
  OPEN: 'open',
  SKIP: 'skip',
  VIEW: 'view',
  SUMMARY: 'summary',
  CONTINUE: 'continue',
  DISCARD: 'discard',
  BACK: 'back',
} as const;

type JobRefinementInteractions =
  (typeof JOB_REFINEMENT_INTERACTIONS)[keyof typeof JOB_REFINEMENT_INTERACTIONS];

const EVENT_NAMES = {
  SINGLE_SELECT: 'search.question_single_select_interaction',
  MULTI_SELECT: 'search.question_multi_select_interaction',
  PHOTO_UPLOAD: 'search.question_photo_upload',
  TEXT_FIELD: 'search.question_text_interaction',
} as const;

const getEventName = (state: State) => {
  const { form, questionIndex } = state;
  const format = form.questions?.[questionIndex].format;
  if (
    [
      QUESTION_FORMAT.SINGLE_SELECT,
      QUESTION_FORMAT.SINGLE_SELECT_VISUAL,
    ].includes(format as QuestionFormat)
  ) {
    return EVENT_NAMES.SINGLE_SELECT;
  }

  if (
    [
      QUESTION_FORMAT.MULTI_SELECT,
      QUESTION_FORMAT.MULTI_SELECT_VISUAL,
    ].includes(format as QuestionFormat)
  ) {
    return EVENT_NAMES.MULTI_SELECT;
  }

  if ([QUESTION_FORMAT.PHOTO_UPLOAD].includes(format as QuestionFormat)) {
    return EVENT_NAMES.PHOTO_UPLOAD;
  }

  return EVENT_NAMES.TEXT_FIELD;
};

const trackEvent = (
  interaction: JobRefinementInteractions,
  state: State,
  overrides = {},
) => {
  const { form, postcode, questionIndex, searchKey } = state;
  const eventName = getEventName(state);
  const body = {
    form_id: form.formId,
    experiment_id: form.experimentId ?? '',
    experiment_name: form.experimentName ?? '',
    variant: form.variant ?? '',
    interaction,
    step: questionIndex + 1,
    search_key: searchKey,
    postcode: postcode ?? '',
    question_id: form.questions ? form.questions[questionIndex].questionId : '',
    question_title: form.questions ? form.questions[questionIndex].title : '',
    trade_category: form.categoryId,
    ...overrides,
  };
  track(eventName, body);
};

const trackOpenJourney = (state: State, overrides: { origin?: string }) => {
  if (overrides?.origin === 'search') {
    track('inline_send_a_job.open', { resource_type: 'search' });
  }
};

const trackOpenSummary = () => {
  track('global.page_view', { pageId: 'JobDetails' });
};

export const trackJobRefinementForm = (
  interaction: JobRefinementInteractions,
  state: State,
  overrides = {},
) => {
  if (interaction === JOB_REFINEMENT_INTERACTIONS.OPEN)
    return trackOpenJourney(state, overrides);
  if (interaction === JOB_REFINEMENT_INTERACTIONS.SUMMARY)
    return trackOpenSummary();
  return trackEvent(interaction, state, overrides);
};
